/**
 * The Icon Drop mocks, in the design canvas's own numbers.
 *
 * Same approach as mobileFallbackScale: every value is the literal pixel the canvas sets,
 * spent as a fraction of the stage rather than as a fixed pixel, so the composition holds
 * its proportions on any handset. The Icon Drop canvas declares a 412 artboard where the
 * device fallback declares 390, so it carries its own divisor instead of borrowing that one.
 */

import { ICON_DROP_WASH_PRESET_LOWER } from './iconDropWash';

/** The width the Icon Drop canvas artboard declares. */
export const ICON_DROP_ARTBOARD_WIDTH = 412;

/** Literal canvas pixels. Spend them through `iconDropPx`, never directly. */
export const ICON_DROP_PX = {
  headline: 28,
  subcopy: 18,
  cta: 18,
  quietAction: 14,

  headlineGap: 10,
  ctaGap: 24,
  quietActionGap: 8,
  quietActionPadding: 8,
  copyInset: 24,

  ctaHeight: 56,
  ctaRadius: 15,
  /** Side inset so "Launch Super Tiny Football" and "Play Now" clear the yellow plate. */
  ctaPaddingX: 20,

  studioLogo: 56,
  /** Fallback cap; BrandCluster contain-boxes studio to 35% of the game mark. */
  studioLogoMaxWidth: 0.24,
  /** Canvas slot for the game mark. Stay at the slot so wide wordmarks are not clipped. */
  gameLogo: 160,
  /** Widest the game mark may run, as a share of the stage. */
  gameLogoMaxWidth: 0.72,
  brandGap: 8,

  poweredBy: 28,
  poweredByInset: 20,

  // Added screen: the installed icon and the toast that announces the add.
  appIcon: 148,
  appIconRadius: 32,
  appIconBorder: 2,
  appName: 20,
  appNameGap: 6,

  /** Clearance from the icon cluster's centre line up to the bottom of the toast. */
  toastLift: 117,
  toastLabel: 15,
  toastGap: 10,
  toastPaddingY: 10,
  toastPaddingLeft: 12,
  toastPaddingRight: 20,
  toastCheck: 26,
  toastCheckGlyph: 15,

  // The success screen's own copy measures: a point under the ad on the subcopy, a larger
  // label on the offer, and less air above it now that the icon carries the upper half.
  successSubcopy: 17,
  successCta: 18,
  successCtaGap: 18,

  // Unavailable screen: the alert mark that leads the copy, and its own tighter measure.
  alertMark: 64,
  alertGlyph: 34,
  alertGap: 20,
  unavailableSubcopy: 16,
  unavailableCopyInset: 36,

  /**
   * Floor under the copy block. The canvas keeps the copy off the bottom edge by this much
   * on short (16:9) phones, where a bare percentage would let it ride over the footer.
   */
  copyBlockFloor: 96,

  backgroundBlur: 2,
} as const;

/** Where the canvas centres the brand cluster, measured down the screen. */
export const ICON_DROP_BRAND_TOP = '34%';

/**
 * The installed icon sits lower than the brand lockup does, nearer the middle of the
 * screen: it is a single square rather than a stacked pair, so the upper third leaves it
 * stranded above the copy instead of filling the space.
 */
export const ICON_DROP_APP_ICON_TOP = '44%';

/** The same line as ICON_DROP_APP_ICON_TOP, measured up from the bottom instead. */
export const ICON_DROP_APP_ICON_BOTTOM = '56%';

/** A share of the stage width. */
export const iconDropScaled = (fraction: number) => `calc(var(--stage-w) * ${fraction})`;

/** A canvas pixel, as a share of the stage. */
export const iconDropPx = (canvasPx: number) =>
  iconDropScaled(canvasPx / ICON_DROP_ARTBOARD_WIDTH);

/**
 * The canvas draws the installed-icon cluster smaller than it wants to read on a phone,
 * where it is the subject of the screen rather than one element on an artboard. Everything
 * in the cluster — tile, label, and the toast that clears it — runs over the canvas by this
 * much, so the group grows as one piece.
 */
export const ICON_DROP_APP_ICON_SCALE = 1.2;

/** A canvas pixel in the installed-icon cluster. */
export const iconDropAppPx = (canvasPx: number) =>
  iconDropPx(canvasPx * ICON_DROP_APP_ICON_SCALE);

/** Same as STF: no extra punch-in. Payday 16:9 letterbox crop is opted out on the stage. */
export const ICON_DROP_MEDIA_ZOOM = 1;

/** Anika UI Shell wash defaults, kept here so existing imports keep compiling. */
export const ICON_DROP_MEDIA_BAND = `${ICON_DROP_WASH_PRESET_LOWER.mediaBandPercent}%`;
export const ICON_DROP_WASH_STOPS = ICON_DROP_WASH_PRESET_LOWER.washStops;
export const ICON_DROP_MEDIA_MASK_STOPS = ICON_DROP_WASH_PRESET_LOWER.maskStops;
